import { useMutation } from '@apollo/client';
import { CheckValue } from 'elements/minute/sidebar/sidebarComponents/atomic/CheckValue';
import { NumberValue } from 'elements/minute/sidebar/sidebarComponents/atomic/NumberValue';
import { ColorPickerButton } from 'elements/minute/sidebar/sidebarComponents/complex/ColorPickerButton';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSectionRow } from 'elements/minute/sidebar/sidebarSection/SidebarSectionRow';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import {
    UpdateWorldMap,
    UpdateWorldMapVariables
} from 'graphql/mutation/__generated__/UpdateWorldMap';
import { UPDATE_WORLD_MAP_MUTATION } from 'graphql/mutation/worldMutation';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { useContext, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';
import { useUpdateEffect } from 'usehooks-ts';

interface Props {
    className?: string;
}

export const GlobalMarkersSettingsSection = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const [color, setColor] = useState<string>(ctxEdit.map?.groupMarkerColor ?? '#fff');
    const [backgroundColor, setBackgroundColor] = useState<string>(
        ctxEdit.map?.groupMarkerBackground ?? '#000'
    );
    const [clusterMaxZoom, setClusterMaxZoom] = useState<number>(
        ctxEdit.map?.groupMarkerClusterMaxZoom ?? 13
    );
    const [clusterRadius, setClusterRadius] = useState<number>(
        ctxEdit.map?.groupMarkerClusterRadius ?? 40
    );
    const [autoHide, setAutoHide] = useState<boolean>(ctxEdit.map?.autoHideIcons ?? false);

    const { getTokenForMapId } = useWorldMapLocalStorage();

    const [updateWorldMutation] = useMutation<UpdateWorldMap, UpdateWorldMapVariables>(
        UPDATE_WORLD_MAP_MUTATION
    );

    useUpdateEffect(() => {
        updateWorldMutation({
            variables: {
                worldMapId: ctxEdit.map?.worldMapId,
                token: getTokenForMapId(ctxEdit.map?.worldMapId),
                map: {
                    groupMarkerColor: color,
                    groupMarkerBackground: backgroundColor,
                    groupMarkerClusterMaxZoom: clusterMaxZoom,
                    groupMarkerClusterRadius: clusterRadius,
                    autoHideIcons: autoHide
                }
            }
        });

        ctxEdit.setMap({
            ...ctxEdit.map,
            groupMarkerColor: color,
            groupMarkerBackground: backgroundColor,
            groupMarkerClusterMaxZoom: clusterMaxZoom,
            groupMarkerClusterRadius: clusterRadius,
            autoHideIcons: autoHide
        });
    }, [color, backgroundColor, clusterMaxZoom, clusterRadius, autoHide]);

    return (
        <S_DebugSection className={props.className}>
            <SidebarSection
                title={Localization.GLOBAL_MARKER_SETTINGS}
                icon={{ iconName: 'location-dot' }}
            >
                <SidebarSubsection
                    title={Localization.PIN_SETTINGS}
                    icon={{ iconName: 'location-dot' }}
                >
                    <SidebarContent>
                        <SidebarSectionRow title={Localization.TEXT_COLOR}>
                            <ColorPickerButton
                                value={color}
                                onChange={(e) => {
                                    setColor(e);
                                }}
                            />
                        </SidebarSectionRow>
                        <SidebarSectionRow title={Localization.BACKGROUND_COLOR}>
                            <ColorPickerButton
                                value={backgroundColor}
                                onChange={(e) => {
                                    setBackgroundColor(e);
                                }}
                            />
                        </SidebarSectionRow>
                        <SidebarSectionRow title={Localization.AUTO_HIDE_LABELS}>
                            <CheckValue
                                label={
                                    clusterMaxZoom > 0
                                        ? Localization.ENABLED
                                        : Localization.DISABLED
                                }
                                value={autoHide}
                                onChange={(e) => {
                                    setAutoHide(e);
                                }}
                            />
                        </SidebarSectionRow>
                        <SidebarSectionRow title={Localization.MARKER_GROUPING}>
                            <CheckValue
                                label={
                                    clusterMaxZoom > 0
                                        ? Localization.ENABLED
                                        : Localization.DISABLED
                                }
                                value={clusterMaxZoom > 0}
                                onChange={(e) => {
                                    if (e) {
                                        setClusterMaxZoom(1);
                                    } else {
                                        setClusterMaxZoom(0);
                                    }
                                }}
                            />
                        </SidebarSectionRow>

                        {clusterMaxZoom > 0 && (
                            <>
                                <SidebarSectionRow title={Localization.MAX_CLUSTERING_ZOOM}>
                                    <NumberValue
                                        value={clusterMaxZoom}
                                        onChange={(e) => {
                                            setClusterMaxZoom(e);
                                        }}
                                    />
                                </SidebarSectionRow>
                                <SidebarSectionRow title={Localization.CLUSTER_RADIUS}>
                                    <NumberValue
                                        value={clusterRadius}
                                        onChange={(e) => {
                                            setClusterRadius(e);
                                        }}
                                    />
                                </SidebarSectionRow>
                            </>
                        )}
                    </SidebarContent>
                </SidebarSubsection>
            </SidebarSection>
        </S_DebugSection>
    );
};

const S_DebugSection = styled.div``;
